const YOUTUBE_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/;

export function nonEmptyString(value) {
  return typeof value === "string" && value.trim().length > 0;
}

export function youtubeVideoId(value) {
  if (!nonEmptyString(value)) return null;

  const candidate = value.trim();
  if (YOUTUBE_ID_PATTERN.test(candidate)) return candidate;

  try {
    const url = new URL(candidate);
    const hostname = url.hostname.replace(/^www\./, "");
    let id = null;

    if (hostname === "youtu.be") {
      [id] = url.pathname.split("/").filter(Boolean);
    } else if (
      hostname === "youtube.com"
      || hostname === "m.youtube.com"
      || hostname === "youtube-nocookie.com"
    ) {
      id = url.searchParams.get("v");
      if (!id) {
        const parts = url.pathname.split("/").filter(Boolean);
        if (["embed", "shorts", "live"].includes(parts[0])) id = parts[1];
      }
    }

    return YOUTUBE_ID_PATTERN.test(id ?? "") ? id : null;
  } catch {
    return null;
  }
}

export function configuredYoutubeVideo(video) {
  return Boolean(
    video
    && nonEmptyString(video.title)
    && youtubeVideoId(video.youtubeId ?? video.url),
  );
}

export function configuredYoutubeSlider(slider) {
  return Boolean(
    slider
    && nonEmptyString(slider.title)
    && Array.isArray(slider.videos)
    && slider.videos.some(configuredYoutubeVideo),
  );
}

export function configuredYoutubePlaceholderSlider(slider) {
  return Boolean(
    slider
    && nonEmptyString(slider.title)
    && Number.isInteger(slider.placeholderSlides)
    && slider.placeholderSlides > 0,
  );
}

export function displayableYoutubeSlider(slider) {
  return configuredYoutubeSlider(slider)
    || configuredYoutubePlaceholderSlider(slider);
}

export function configuredJingle(jingle, activePhase) {
  if (!jingle?.enabled || !nonEmptyString(jingle.src)) return false;
  if (!Array.isArray(jingle.phases) || !jingle.phases.includes(activePhase)) {
    return false;
  }

  return true;
}

export function youtubeEmbedUrl(
  video,
  { autoplay = false, loop = false, muted = false } = {},
) {
  const id = youtubeVideoId(video?.youtubeId ?? video?.url);
  if (!id) return null;

  const params = new URLSearchParams({
    autoplay: autoplay ? "1" : "0",
    controls: "1",
    enablejsapi: "1",
    modestbranding: "1",
    playsinline: "1",
    rel: "0",
  });

  if (muted) params.set("mute", "1");
  if (loop) {
    params.set("loop", "1");
    params.set("playlist", id);
  }

  return `https://www.youtube-nocookie.com/embed/${id}?${params.toString()}`;
}

export function createYoutubeIframe(video, options = {}) {
  const src = youtubeEmbedUrl(video, options);
  if (!src) return null;

  const iframe = document.createElement("iframe");
  iframe.src = src;
  iframe.title = video.title;
  iframe.loading = options.loading ?? "lazy";
  iframe.referrerPolicy = "strict-origin-when-cross-origin";
  iframe.allow = "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture";
  iframe.allowFullscreen = true;
  return iframe;
}
